import React, { forwardRef, useState } from 'react'

import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import Icon, { ICONS } from './Icon'
import Input from './Input'

type IconInputProps = React.ComponentProps<typeof Input> & {
  CustomInput?: React.ComponentType<React.ComponentProps<typeof Input>>
  customInputProps?: Record<string, unknown>
  icon: keyof typeof ICONS | React.ReactElement
  iconLabel?: string
  onIconClick?: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick']
}

const InputContainer = styled.div`
  position: relative;
  display: grid;

  input {
    padding-right: 3.5rem;
  }

  input::-ms-reveal,
  input::-ms-clear {
    display: none;
  }
`

const IconContainer = styled.div<{ interactable?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0.375rem;
  display: grid;
  place-items: center;

  ${props =>
    !props.interactable &&
    css`
      pointer-events: none;
    `}
`

const IconTouchTarget = styled.button<{
  iconColor: string
  interactable?: boolean
}>`
  width: 2.75rem;
  height: 2.75rem;
  display: grid;
  place-items: center;
  background: transparent;
  border: none;
  padding: 0;
  color: ${props => props.iconColor};
  font-size: 1.5rem;

  outline-style: none;
  ${props => props.theme.selectors.keyboard} &:focus {
    outline-style: auto;
  }

  ${props =>
    props.interactable &&
    css`
      cursor: pointer;
    `}
`

const IconInput = forwardRef<HTMLInputElement, IconInputProps>(
  ({ CustomInput = Input, customInputProps, icon, iconLabel, onIconClick, ...props }, ref) => {
    const [isInputFocused, setInputFocused] = useState(false)
    const theme = useTheme()

    const interactable = onIconClick != null

    const iconColor = (() => {
      if (props.invalid) {
        return theme.colors.error
      }
      if (props.disabled) {
        return theme.colors.disabledIcon
      }
      if (isInputFocused) {
        return theme.colors.primary
      }
      return theme.colors.label
    })()

    const inputProps = { ...customInputProps, ...props }

    return (
      <InputContainer>
        <CustomInput
          {...inputProps}
          onFocus={(event: React.FocusEvent<HTMLInputElement, Element>) => {
            props.onFocus?.(event)
            setInputFocused(true)
          }}
          onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
            props.onBlur?.(event)
            setInputFocused(false)
          }}
          ref={ref}
        />
        <IconContainer interactable={interactable}>
          <IconTouchTarget
            as={interactable ? 'button' : 'div'}
            type="button"
            onClick={onIconClick}
            aria-label={iconLabel}
            interactable={interactable}
            iconColor={iconColor}
          >
            {typeof icon === 'string' ? <Icon name={icon} /> : icon}
          </IconTouchTarget>
        </IconContainer>
      </InputContainer>
    )
  }
)

export default IconInput
